import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { FieldInputProps, useFormikContext } from "formik";
import { Box, Skeleton, Typography } from "@mui/material";
import { GridRowModel } from "@mui/x-data-grid-pro";

import { Item, RemoteLookupFormDetails } from "../interfaces";
import { GenericObject } from "../../services/Price";
import { getSelectFilterValues } from "../utils";

/**
 * A value the user does not pick but that follows other fields: queried from
 * an API as soon as the fields it depends on are filled, written into another
 * field, optionally previewed. Nothing found is an error that blocks the form.
 */
const RemoteLookup = ({
  filter,
  field,
  row,
}: {
  filter: RemoteLookupFormDetails;
  field: FieldInputProps<Item | null | undefined>;
  row?: GridRowModel;
}) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<GenericObject>();

  // The form values are a new object on every change, so the params are
  // rebuilt through their serialization: same inputs, same object, and the
  // query and the effect below only react to a real change.
  const paramsKey = JSON.stringify(filter.lookupParams(values, row));
  const params = useMemo<GenericObject | null>(
    () => JSON.parse(paramsKey),
    [paramsKey]
  );

  const query = useQuery(
    ["remote-lookup", filter.api, params],
    () => getSelectFilterValues<Item>(filter.api, params ?? undefined),
    { enabled: !!params }
  );

  const item = useMemo<Item | null>(() => {
    if (!params || !query.data) return null;
    const items = query.data.results;
    return filter.pick ? filter.pick(items, values, row) : items[0] ?? null;
  }, [params, query.data, filter, values, row]);

  useEffect(() => {
    // Nothing to look up yet, or a new lookup pending: the previous result no
    // longer matches the inputs, so drop it rather than let it be submitted.
    const found = params && query.isSuccess ? item : null;
    setFieldValue(field.name, found);
    setFieldValue(filter.writeTo.field, filter.writeTo.value(found));
  }, [item, query.isSuccess, params, field.name, filter, setFieldValue]);

  const notFound = !!params && query.isSuccess && !item;
  // A lookup without preview only ever shows its error: no skeleton for it,
  // and no space at all while there is nothing to say.
  const showPreview = !!filter.render && !!params;
  const showSkeleton = showPreview && query.isLoading;
  if (!showSkeleton && !(item && filter.render) && !notFound) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, m: 1 }}>
      {showSkeleton && <Skeleton variant="rounded" width={200} height={24} />}
      {item && filter.render && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {intl.formatMessage({ id: filter.label })}
          </Typography>
          {filter.render(item, intl)}
        </Box>
      )}
      {notFound && (
        <Typography variant="body2" sx={{ color: "error.main" }}>
          {intl.formatMessage({ id: filter.notFoundLabel })}
        </Typography>
      )}
    </Box>
  );
};

export default RemoteLookup;
